import { forwardRef, Fragment, ReactNode, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import styles from './searchable-select.module.scss';

export type SearchableSelectOption = {
  label: string;
  value: string | undefined;
};

export type SearchableSelectOptionGroup = {
  groupLabel: string;
  value: SearchableSelectOption[];
};

/* pressing the list's own scrollbar or padding would move focus out of the input (the container
 * has no tabIndex), and onBlur would close the list mid-scroll. Options are left alone: they take
 * focus as before, so the input blurs and a later click on it reopens the list */
const keepFocusOnContainerPress: React.MouseEventHandler<HTMLDivElement> = e => {
  if (e.target === e.currentTarget) e.preventDefault();
};

type SearchableSelectProps = {
  onValueSelected: (val: string) => void;
  value: string | undefined;
  options: SearchableSelectOption[] | SearchableSelectOptionGroup[];
  disabled?: boolean;
  required?: boolean;
  inputClass?: string;
  containerClass?: string;
  label?: ReactNode;
  id?: string;
  dropdownId?: string;
  onClear?: () => void;
  onTextChange?: React.ChangeEventHandler<HTMLInputElement>;
  ['data-cy']?: string;
};

const SearchableSelect = forwardRef<Pick<HTMLInputElement, 'setCustomValidity'>, SearchableSelectProps>(
  ({ options, disabled, required, value, onValueSelected, inputClass, containerClass, ...props }, forwardedRef) => {
    const [filteredOptions, setFilteredOptions] = useState(options);
    const [textValue, setTextValue] = useState(value);
    const [searching, setSearching] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    /* prettier-ignore */
    useImperativeHandle(forwardedRef, () => ({
      setCustomValidity: (error: string) => {
        inputRef.current?.setCustomValidity(error);
        inputRef.current?.reportValidity();
    }}), []);

    const isGrouped = (opts: SearchableSelectOption[] | SearchableSelectOptionGroup[]): boolean => {
      return opts.some(opt => Object.keys(opt).includes('groupLabel'));
    };

    const setToInputValue = useCallback(() => {
      let option: SearchableSelectOption | undefined;
      if (isGrouped(options)) {
        // Grouped
        option = (options as SearchableSelectOptionGroup[]).flatMap(opt => opt.value).find(opt => opt.value === value);
      } else {
        option = (options as SearchableSelectOption[]).find(o => o.value === value);
      }
      /* retired/inactive codes aren't in the (active-only) options, so the label lookup misses —
       * fall back to the raw value so persisted records still show their stored code (zero-churn)
       * instead of going blank. Works for non-admins too, who never receive the full lookup list. */
      setTextValue(option?.label ?? value ?? '');
      setSearching(false);
    }, [options, value]);

    const filterOptions = useCallback(
      (
        searchString: string,
        opts: SearchableSelectOption[] | SearchableSelectOptionGroup[]
      ): SearchableSelectOption[] | SearchableSelectOptionGroup[] => {
        if (isGrouped(opts)) {
          // Grouped
          const filteredGroups: SearchableSelectOptionGroup[] = (opts as SearchableSelectOptionGroup[])
            .map(opt => ({
              ...opt,
              value: opt.value.filter(val => val.label.toLowerCase().startsWith(searchString.toLowerCase()))
            }))
            .filter(opt => !!opt.value.length);
          return filteredGroups;
        } else {
          // Ungrouped
          return (opts as SearchableSelectOption[]).filter(opt =>
            opt.label.toLowerCase().startsWith(searchString.toLowerCase())
          );
        }
      },
      []
    );

    const onFocus = useCallback<React.FocusEventHandler<HTMLInputElement>>(() => {
      setFilteredOptions(options);
      setSearching(true);
    }, [options]);

    const onTextChange = useCallback<React.ChangeEventHandler<HTMLInputElement>>(
      e => {
        setTextValue(e.currentTarget.value);
        setFilteredOptions(filterOptions(e.currentTarget.value, options));
      },
      [filterOptions, options]
    );

    const onOptionClicked = useCallback<React.MouseEventHandler<HTMLDivElement>>(
      e => {
        e.stopPropagation();
        const val = e.currentTarget.dataset['value'];
        onValueSelected(val ?? '');
        setTextValue(e.currentTarget.textContent ?? '');
        setSearching(false);
      },
      [onValueSelected]
    );

    const onBlur = useCallback<React.FocusEventHandler<HTMLInputElement>>(
      e => {
        if (!e.relatedTarget?.classList.contains(styles['dropdown-option'])) {
          setToInputValue();
        }
      },
      [setToInputValue]
    );

    useEffect(() => {
      setToInputValue();
    }, [setToInputValue]);

    return (
      <div className={styles['searchable-select'] + ' ' + (containerClass ?? '')}>
        <input
          ref={inputRef}
          type="text"
          disabled={!!disabled}
          value={textValue ?? ''}
          onFocus={onFocus}
          onChange={props.onTextChange ?? onTextChange}
          onBlur={onBlur}
          required={!!required}
          className={`${!textValue ? 'blank' : ''} ${inputClass ?? ''}`.trim()}
          id={props.id}
          data-cy={props['data-cy']}
        />
        {!!props.label && (
          <label htmlFor={props.id}>
            {props.label}
            {!!required && ' *'}
          </label>
        )}
        {!disabled && Boolean(textValue || value) && (
          <svg
            className={styles['clear-btn']}
            focusable="false"
            aria-hidden="true"
            viewBox="0 0 24 24"
            onClick={() => {
              props.onClear?.();
              onValueSelected('');
              setTextValue('');
              setFilteredOptions(options);
              setTimeout(() => {
                inputRef.current?.focus();
              }, 0);
            }}
          >
            <path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"></path>
          </svg>
        )}
        {searching && (
          <div id={props.dropdownId} className={styles.dropdown} onMouseDown={keepFocusOnContainerPress}>
            {!isGrouped(filteredOptions) &&
              (filteredOptions as SearchableSelectOption[]).map((option, i) => (
                <div
                  key={option.value}
                  tabIndex={i}
                  className={`pointer ${styles['dropdown-option']}`}
                  onClick={onOptionClicked}
                  data-value={option.value}
                  data-cy={`${props['data-cy'] ?? 'searchable-select'}-option-${option.value}`}
                >
                  {option.label}
                </div>
              ))}
            {isGrouped(filteredOptions) &&
              (filteredOptions as SearchableSelectOptionGroup[]).map((group, i) => (
                <Fragment key={group.groupLabel}>
                  <div className={`${styles['dropdown-group-header']}`}>{group.groupLabel}</div>
                  {group.value.map((option, j) => (
                    <div
                      key={option.value}
                      tabIndex={i * filteredOptions.length + j}
                      className={`pointer ${styles['dropdown-option']}`}
                      onClick={onOptionClicked}
                      data-value={option.value}
                      data-cy={`${props['data-cy'] ?? 'searchable-select'}-option-${option.value}`}
                    >
                      {option.label}
                    </div>
                  ))}
                </Fragment>
              ))}
          </div>
        )}
      </div>
    );
  }
);

export default SearchableSelect;
